import React, { useState, useEffect, useRef } from "react";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { IoMdAdd } from "react-icons/io";
import { LuDownload } from "react-icons/lu";
import gsap from "gsap";
import { motion, AnimatePresence } from "framer-motion";
import bgTop from "../assets/design_2.png";
import bgCircle from "../assets/design_3.png";
import bgBottom from "../assets/design_1.png";
import "../css/GroomingCard.css";

const GroomingCard = ({
  id,
  title,
  subscribedPrice,
  unsubscribedPrice,
  services: initialServices,
  tag,
  isEditable = false,
  onClose,
  onSave,
  onEdit,
  onDelete,
}) => {
  const cardRef = useRef(null);
  const [services, setServices] = useState(initialServices || []);
  const [showInput, setShowInput] = useState(false);
  const [newService, setNewService] = useState("");
  const [isEditingSubscribed, setIsEditingSubscribed] = useState(false);
  const [isEditingUnsubscribed, setIsEditingUnsubscribed] = useState(false);
  const [editedSubscribedPrice, setEditedSubscribedPrice] =
    useState(subscribedPrice);
  const [editedUnsubscribedPrice, setEditedUnsubscribedPrice] =
    useState(unsubscribedPrice);

  useEffect(() => {
    gsap.fromTo(
      cardRef.current,
      { opacity: 0, y: 40, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "power3.out" }
    );
  }, []);

  useEffect(() => {
    if (isEditable) {
      setServices(initialServices || []);
      setEditedSubscribedPrice(subscribedPrice);
      setEditedUnsubscribedPrice(unsubscribedPrice);
      setShowInput(false);
      setNewService("");
      setIsEditingSubscribed(false);
      setIsEditingUnsubscribed(false);
    }
  }, [initialServices, subscribedPrice, unsubscribedPrice, isEditable]);

  const firstColumn = services.slice(0, 7);
  const secondColumn = services.slice(7);

  const renderBadge = () => {
    const tagMap = {
      popular: "grooming-popular-badge",
      great_deals: "grooming-greatDeals-badge",
      steal_deals: "grooming-stealDeals-badge",
    };
    const className = tagMap[tag?.trim()];
    return className ? (
      <motion.span
        className={className}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
      >
        {tag.replace("_", " ")}
      </motion.span>
    ) : null;
  };

  const handleServiceDelete = (indexToDelete) => {
    gsap.to(`.service-item-${indexToDelete}`, {
      opacity: 0,
      x: -20,
      duration: 0.3,
      onComplete: () => {
        setServices((prev) => prev.filter((_, i) => i !== indexToDelete));
      },
    });
  };

  const handleAddService = () => {
    if (!newService.trim()) return;
    setServices((prev) => [...prev, newService.trim()]);
    setNewService("");
    setShowInput(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleAddService();
  };

  const handlePriceKeyDown = (e, type) => {
    if (e.key === "Enter") {
      if (type === "subscribed") setIsEditingSubscribed(false);
      if (type === "unsubscribed") setIsEditingUnsubscribed(false);
    }
  };

  const handleSaveClick = () => {
    onSave({
      id,
      title,
      subscribed_user_offer_amount: Number(editedSubscribedPrice),
      package_amount: Number(editedUnsubscribedPrice),
      services,
      tag,
    });
  };

  if (isEditable) {
    return (
      <AnimatePresence>
        <motion.div
          className="editable-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            ref={cardRef}
            className="grooming-card"
            initial={{ scale: 0.9, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <img
              src={bgTop}
              alt="top design"
              className="grooming-card-top-bg"
            />
            {renderBadge()}

            {/* Pricing Circle */}
            <div className="grooming-card-pricing">
              <div className="grooming-price-circle">
                <img
                  src={bgCircle}
                  alt="circle design"
                  className="grooming-circle-bg"
                />

                <motion.div
                  className="grooming-circle-content"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <p>Subscribed User Price</p>
                  <h4>
                    {isEditingSubscribed ? (
                      <motion.input
                        type="number"
                        value={editedSubscribedPrice}
                        onChange={(e) =>
                          setEditedSubscribedPrice(e.target.value)
                        }
                        onKeyDown={(e) => handlePriceKeyDown(e, "subscribed")}
                        onBlur={() => setIsEditingSubscribed(false)}
                        autoFocus
                        className="price-input"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    ) : (
                      <>
                        ₹{editedSubscribedPrice}
                        <button
                          className="grooming-price-edit-btn"
                          onClick={() => setIsEditingSubscribed(true)}
                        >
                          <FiEdit size={18} color="#000" />
                        </button>
                      </>
                    )}
                  </h4>

                  <p>Unsubscribed User Price</p>
                  <h4 className="unsub-price">
                    {isEditingUnsubscribed ? (
                      <motion.input
                        type="number"
                        value={editedUnsubscribedPrice}
                        onChange={(e) =>
                          setEditedUnsubscribedPrice(e.target.value)
                        }
                        onKeyDown={(e) => handlePriceKeyDown(e, "unsubscribed")}
                        onBlur={() => setIsEditingUnsubscribed(false)}
                        autoFocus
                        className="price-input"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    ) : (
                      <>
                        ₹{editedUnsubscribedPrice}
                        <button
                          className="grooming-price-edit-btn"
                          onClick={() => setIsEditingUnsubscribed(true)}
                        >
                          <FiEdit size={18} color="#000" />
                        </button>
                      </>
                    )}
                  </h4>
                </motion.div>
              </div>

              {/* Service List */}
              <div className="services-columns">
                <ul className="grooming-services-list">
                  {services.map((service, index) => (
                    <motion.li
                      key={index}
                      className={`service-item-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <span
                        className="grooming-delete-icon"
                        onClick={() => handleServiceDelete(index)}
                      >
                        −
                      </span>{" "}
                      {service}
                    </motion.li>
                  ))}

                  {/* ✅ Input directly under last service */}
                  {showInput && (
                    <li className="new-service-input-below">
                      <input
                        type="text"
                        value={newService}
                        onChange={(e) => setNewService(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Text Here..."
                        autoFocus
                      />
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <motion.div
              className="grooming-edit-btns"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.button
                className="add-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowInput(true)}
              >
                <IoMdAdd className="add-package-icon" color="#EBA8BF" /> Add
              </motion.button>

              <motion.button
                className="save-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveClick}
              >
                <LuDownload className="add-package-icon" color="#EBA8BF" /> Save
              </motion.button>
            </motion.div>

            <motion.span
              className="close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              Close
            </motion.span>

            <img
              src={bgBottom}
              alt="bottom design"
              className="card-bottom-bg"
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <motion.div
      ref={cardRef}
      className="grooming-card-wrapper"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <div className="grooming-card-header">
        <h3>{title}</h3>
        <div className="grooming-card-actions">
          <button className="grooming-edit-btn" onClick={onEdit}>
            <FiEdit size={20} color="#000" />{" "}
            <span className="grooming-edit">Edit</span>
          </button>
          <button className="grooming-delete-btn" onClick={onDelete}>
            <RiDeleteBin6Fill size={32} />
          </button>
        </div>
      </div>

      <div className="grooming-card">
        <img src={bgTop} alt="top design" className="card-top-bg" />
        {renderBadge()}

        <div className="grooming-card-pricing">
          <div className="grooming-price-circle">
            <img
              src={bgCircle}
              alt="circle design"
              className="grooming-circle-bg"
            />
            <div className="grooming-circle-content">
              <p>Subscribed User Price</p>
              <h4>₹{subscribedPrice}</h4>
              <p>Unsubscribed User Price</p>
              <h4 className="unsub-price">₹{unsubscribedPrice}</h4>
            </div>
          </div>

          <div className="services-columns">
            <ul className="grooming-services-list">
              {firstColumn.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>

            {secondColumn.length > 0 && (
              <ul className="grooming-services-list">
                {secondColumn.map((service, index) => (
                  <li key={index + 7}>{service}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <img src={bgBottom} alt="bottom design" className="card-bottom-bg" />
      </div>
    </motion.div>
  );
};

export default GroomingCard;
